import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShoppingBag, Trash2, Plus, Minus, ArrowLeft, CreditCard, Truck, Ticket, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/context/I18nContext";
import { OrdersInbox } from "@/components/orders/OrdersInbox";

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

export default function Cart() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [cart, setCart] = useState(null);
  const [checkout, setCheckout] = useState({ payment_mode: "findpay", address: "", phone: user?.mobile || "" });
  const idemKey = useRef(null);
  useEffect(() => { if (user?.mobile) setCheckout((c) => (c.phone ? c : { ...c, phone: user.mobile })); }, [user?.mobile]);   // one key per checkout attempt — double taps / retries return the same orders
  const [busy, setBusy] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try { const r = await api.get("/commerce/cart"); setCart(r.data); } catch {}
  };
  useEffect(() => { if (user) load(); }, [user]);

  const update = async (item_id, qty) => {
    try { const r = await api.post("/commerce/cart/update", { item_id, qty }); setCart({ lines: r.data.lines, summary: r.data.summary }); }
    catch { toast.error("Update failed"); }
  };

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    setCodeBusy(true);
    try {
      const r = await api.post("/commerce/cart/apply-code", { code: codeInput.trim().toUpperCase() });
      setCart({ lines: r.data.lines, summary: r.data.summary });
      toast.success(`${r.data.summary.applied_code.discount_pct}% champion discount applied!`);
      setCodeInput("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setCodeBusy(false); }
  };

  const removeCode = async () => {
    setCodeBusy(true);
    try {
      const r = await api.delete("/commerce/cart/code");
      setCart({ lines: r.data.lines, summary: r.data.summary });
      toast.info("Discount code removed");
    } catch { toast.error("Failed to remove"); }
    finally { setCodeBusy(false); }
  };

  const submit = async () => {
    if (!cart?.lines?.length) return;
    setBusy(true);
    if (!idemKey.current) idemKey.current = `ck_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    try {
      const r = await api.post("/commerce/checkout", { ...checkout, idempotency_key: idemKey.current });
      idemKey.current = null;
      const n = r.data.orders?.length || 0;
      toast.success(checkout.payment_mode === "cod" ? t("cart.placed_cod", "{n} order(s) placed · cash on delivery", { n })
        : t("cart.placed_findpay", "{n} order(s) placed · pay each in Orders (test mode)", { n }));
      nav("/orders");
    } catch (e) {
      if (e?.response?.status === 409) idemKey.current = null;   // conflicting reuse → next attempt gets a fresh key
      toast.error(e?.response?.data?.detail || t("cart.checkout_failed", "Checkout failed"));
    }
    finally { setBusy(false); }
  };

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to view cart.</div>;
  if (!cart) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 pt-4 pb-40" data-testid="cart-page">
      <Link to="/apps" className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back</Link>
      <h1 className="heading text-2xl font-black text-stone-900 mt-2">{t("cart.title", "Cart")}</h1>

      {cart.lines.length === 0 ? (
        <div className="text-center py-20 text-stone-500">
          <ShoppingBag className="mx-auto mb-2 text-stone-300" size={32} />
          Cart is empty. Add items from a store.
        </div>
      ) : (
        <>
          <div className="mt-4 bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
            {cart.lines.map(l => (
              <div key={l.item_id} className="p-3 flex items-center gap-3" data-testid={`cart-line-${l.item_id}`}>
                {l.image ? <img loading="lazy" src={l.image} alt="" className="w-14 h-14 rounded-lg object-cover" /> :
                  <div className="w-14 h-14 rounded-lg bg-stone-200" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{l.name}</div>
                  <div className="text-xs text-stone-500">{t("cart.by_seller", "by {seller}", { seller: l.seller_name })}</div>
                  <div className="text-sm font-black text-[#E05E36]">{rupees(l.unit_price_paise)}</div>
                </div>
                <div className="flex items-center gap-1 bg-stone-100 rounded-full">
                  <button onClick={() => update(l.item_id, Math.max(0, l.qty - 1))} className="p-2 hover:bg-stone-200 rounded-full" data-testid={`cart-dec-${l.item_id}`}><Minus size={12} /></button>
                  <span className="text-sm font-bold w-6 text-center">{l.qty}</span>
                  <button onClick={() => update(l.item_id, l.qty + 1)} className="p-2 hover:bg-stone-200 rounded-full" data-testid={`cart-inc-${l.item_id}`}><Plus size={12} /></button>
                </div>
                <button onClick={() => update(l.item_id, 0)} className="text-red-500 p-2 hover:bg-red-50 rounded-full" data-testid={`cart-remove-${l.item_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <div className="p-3 space-y-2">
              {cart.summary.applied_code ? (
                <div className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 flex items-center gap-2" data-testid="cart-applied-code">
                  <Ticket size={13} className="text-emerald-700"/>
                  <span className="text-[11px] font-black text-emerald-900">{cart.summary.applied_code.code}</span>
                  <span className="text-[10px] text-emerald-700 font-bold">−{cart.summary.applied_code.discount_pct}% champion discount</span>
                  <button onClick={removeCode} disabled={codeBusy} className="ml-auto text-emerald-700 hover:text-emerald-900" data-testid="cart-remove-code" title="Remove code">
                    {codeBusy ? <Loader2 size={12} className="animate-spin"/> : <X size={13}/>}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Have a TAP-XXXX champion code?"
                    value={codeInput}
                    onChange={e => setCodeInput(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === "Enter" && applyCode()}
                    className="flex-1 rounded-full text-sm h-9"
                    data-testid="cart-code-input"
                  />
                  <Button onClick={applyCode} disabled={codeBusy || !codeInput.trim()} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-black h-9" data-testid="cart-code-apply">
                    {codeBusy ? <Loader2 size={12} className="animate-spin"/> : <Ticket size={12} className="mr-1"/>} Apply
                  </Button>
                </div>
              )}
              <div className="flex justify-between text-sm text-stone-600">
                <span>{t("cart.subtotal", "Subtotal")}</span>
                <span>{rupees(cart.summary.subtotal_paise)}</span>
              </div>
              {cart.summary.discount_paise > 0 && (
                <div className="flex justify-between text-sm text-emerald-700 font-bold" data-testid="cart-discount-line">
                  <span>Discount</span>
                  <span>− {rupees(cart.summary.discount_paise)}</span>
                </div>
              )}
              <div className="flex justify-between items-center font-black pt-1 border-t border-stone-100">
                <span>{t("cart.total", "Total")}</span>
                <span className="text-lg text-[#E05E36]" data-testid="cart-total">{rupees(cart.summary.total_paise ?? cart.summary.subtotal_paise)}</span>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">{t("cart.delivery", "Delivery details")}</div>
            <p className="text-[11px] text-stone-500">{t("cart.split_note", "One order is created per seller. Each seller sees only their own order.")}</p>
            <Input placeholder={t("cart.phone", "Phone number")} value={checkout.phone} onChange={e => setCheckout({ ...checkout, phone: e.target.value })} data-testid="cart-phone" />
            <Textarea placeholder={t("cart.address", "Delivery address")} value={checkout.address} onChange={e => setCheckout({ ...checkout, address: e.target.value })} rows={3} data-testid="cart-address" />

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setCheckout({ ...checkout, payment_mode: "findpay" })}
                className={`p-3 rounded-xl border-2 text-left ${checkout.payment_mode === "findpay" ? "border-[#E05E36] bg-[#E05E36]/5" : "border-stone-200"}`}
                data-testid="cart-pm-findpay"
              >
                <CreditCard size={16} className="text-[#E05E36]" />
                <div className="font-bold text-sm mt-1">{t("cart.pm_findpay", "FindPay")}</div>
                <div className="text-[10px] font-black text-amber-800">{t("pay.test_mode", "TEST MODE · no real money")}</div>
              </button>
              <button
                onClick={() => setCheckout({ ...checkout, payment_mode: "cod" })}
                className={`p-3 rounded-xl border-2 text-left ${checkout.payment_mode === "cod" ? "border-[#E05E36] bg-[#E05E36]/5" : "border-stone-200"}`}
                data-testid="cart-pm-cod"
              >
                <Truck size={16} className="text-[#E05E36]" />
                <div className="font-bold text-sm mt-1">{t("orders.cod", "Cash on delivery")}</div>
                <div className="text-[11px] text-stone-500">{t("cart.pm_cod_hint", "Pay when delivered")}</div>
              </button>
            </div>

            <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="cart-checkout">
              {busy ? <Loader2 className="animate-spin" size={16} /> : `${t("cart.checkout", "Checkout")} · ${rupees(cart.summary.total_paise ?? cart.summary.subtotal_paise)}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function Orders() {
  return <OrdersInbox />;
}
